import {
  customerTypeEnum,
  opportunityStageEnum,
  propertyTypeEnum,
  visitAttendees,
  visitCommissions,
  visits,
  visitTasks,
  visitTaskSteps,
} from '@markku/db'
import {
  calculateFinalPrice,
  removeVat,
  sumBy,
  type TimeRange,
} from '@markku/utils'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'
import { withAttachments } from './attachment'
import { phoneNumberSchema } from './common'
import { propertyVentilationSchema, selectVisitPreInfoSchema } from './preInfo'
import {
  isVentilationVisit,
  isVentilationWork,
  PRESET_FORM_VALUE_SCHEMAS,
  type ServicePresetKey,
} from './servicePreset'
import { attributionPayloadSchema } from './acquisition'
import {
  selectVisitLineItemWithProductSchema,
  type VisitLineItem,
} from './visitLineItem'

// Predefined cancellation reasons. Selected reasons (or free-text "Muu syy"
// content) are persisted verbatim into visits.cancelReason — the dropdown is
// just a UI shortcut for the most common cases.
export const VISIT_CANCEL_REASONS = [
  'Asiakas perui',
  'Asiakas ei tavoitettu',
  'Kohteeseen ei pääsyä',
  'Asiakas siirsi käynnin',
  'Sääolosuhteet',
  'Työntekijä estynyt',
  'Tuplabuukkaus',
] as const

// Work content item schema
export const visitWorkContentItemSchema = z.object({
  key: z.string(),
  label: z.string().optional(),
})

export type VisitWorkContentItem = z.infer<typeof visitWorkContentItemSchema>

// Ventilation measurement protocol (Mittauspöytäkirja) schemas
export const airMeasurementSchema = z.object({
  quantity: z.string().optional(),
  valveType: z.string().optional(),
  airSpeed: z.string().optional(),
  pressure: z.string().optional(),
  position: z.string().optional(),
  kValue: z.string().optional(),
  adjustedFlow: z.string().optional(),
  requiredFlow: z.string().optional(),
  differenceLs: z.string().optional(),
  differencePercent: z.string().optional(),
  ductShape: z.enum(['round', 'rectangular']).optional(),
  ductDiameter: z.string().optional(),
  ductWidth: z.string().optional(),
  ductHeight: z.string().optional(),
})

export const exhaustAirMeasurementSchema = airMeasurementSchema.extend({
  envelopePressure: z.string().optional(),
})

export const measurementRoomSchema = z.object({
  id: z.string(),
  name: z.string(),
  supplyAir: airMeasurementSchema.optional(),
  exhaustAir: exhaustAirMeasurementSchema.optional(),
  notes: z.string().optional(),
})

export const mittauspoytakirjaDataSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  measurementDate: z.string().optional(),
  measurers: z.string().optional(),
  outdoorTemperature: z.string().optional(),
  windSpeed: z.string().optional(),
  measurementDevices: z.string().optional(),
  calibrationValid: z.string().optional(),
  hvacMachines: z.string().optional(),
  measurementSpeed: z.string().optional(),
  planDate: z.string().optional(),
  rooms: z.array(measurementRoomSchema),
  uploadedPdfAttachmentId: z.number().optional(),
  // Optimistic-lock counter, server-owned. The client echoes back the version
  // it loaded; visit.saveMeasurementReport rejects the write if the stored
  // report has moved on. Absent on reports written before this existed.
  version: z.number().optional(),
})

export const saveMeasurementReportSchema = z.object({
  visitId: z.number(),
  report: mittauspoytakirjaDataSchema,
  // Set after the measurer has been told their base is stale and chose to
  // keep their own copy anyway. Without it a refused save would be a dead
  // end: the version on screen can never catch up on its own.
  overwrite: z.boolean().optional(),
})

export const deleteMeasurementReportSchema = z.object({
  visitId: z.number(),
  reportId: z.string(),
  version: z.number().optional(),
})

// Auto-generate base schemas
export const selectVisitSchema = createSelectSchema(visits, {
  // Make internal fields optional so they can be filtered out (set to undefined)
  internalNotes: z.string().nullable().optional(),
  internalSafetyNotices: z.string().nullable().optional(),
  internalSafetyNoticesReviewedAt: z.string().nullable().optional(),
  internalSafetyNoticesReviewedBy: z.number().nullable().optional(),
  internalSafetyNoticesReviewComment: z.string().nullable().optional(),
  internalFireDepartmentNotifiedAt: z.string().nullable().optional(),
  internalFireDepartmentNotifiedBy: z.number().nullable().optional(),
  internalSalesNotes: z.string().nullable().optional(),
  // INV-7: role-gated attribution column
  internalAcquisitionData: z.unknown().optional(),
  workContent: z.array(visitWorkContentItemSchema).nullable().optional(),
  measurementReportData: z
    .array(mittauspoytakirjaDataSchema)
    .nullable()
    .optional(),
})
export const insertVisitSchema = createInsertSchema(visits, {
  workContent: z.array(visitWorkContentItemSchema).nullable().optional(),
  // Backwards compatibility: accept both single object (old format) and array (new format)
  measurementReportData: z.preprocess((val) => {
    if (val === null || val === undefined) return val
    const arr = Array.isArray(val) ? val : [val]
    return arr.map((item: Record<string, unknown>) =>
      item.id ? item : { ...item, id: crypto.randomUUID() },
    )
  }, z.array(mittauspoytakirjaDataSchema).nullable().optional()),
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  // Stamped by the booking widget alone. Out of the write schemas so the KPI's
  // conversion count can't be edited through the visit CRUD endpoints.
  createdVia: true,
})

export const selectVisitTaskSchema = createSelectSchema(visitTasks)
export const insertVisitTaskSchema = createInsertSchema(visitTasks).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})

export const selectVisitAttendeeSchema = createSelectSchema(visitAttendees)
export const insertVisitAttendeeSchema = createInsertSchema(
  visitAttendees,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})

export const selectVisitTaskStepSchema = createSelectSchema(visitTaskSteps)
export const insertVisitTaskStepSchema = createInsertSchema(
  visitTaskSteps,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})

export const selectVisitCommissionSchema = createSelectSchema(visitCommissions)
export const insertVisitCommissionSchema = createInsertSchema(
  visitCommissions,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})
export const updateVisitCommissionSchema = insertVisitCommissionSchema.partial()

// Visit task with steps
export const selectVisitTaskWithStepsSchema = selectVisitTaskSchema.extend({
  steps: z.array(selectVisitTaskStepSchema).optional(),
})

// Visit with relations
export const selectVisitWithRelationsSchema = withAttachments(
  selectVisitSchema.extend({
    subscription: z
      .object({
        id: z.number(),
        propertyId: z.number(),
        displayName: z.string().nullable().optional(),
        startDate: z.string(),
        endDate: z.string().nullable(),
        status: z.enum(['proposal', 'active', 'paused', 'cancelled']),
        additionalTerms: z.string().nullable(),
        subscriptionPackageVersions: z
          .array(
            z.object({
              servicePackageVersionId: z.number(),
              sortOrder: z.number().nullable(),
            }),
          )
          .optional(),
      })
      .nullable()
      .optional(),
    property: z
      .object({
        id: z.number(),
        customerId: z.number(),
        propertyType: z.enum(propertyTypeEnum.enumValues),
        pets: z.string().nullable().optional(),
        ventilation: propertyVentilationSchema.nullable().optional(),
        customer: z.object({
          id: z.number(),
          name: z.string(),
          type: z.enum(customerTypeEnum.enumValues),
          billingMethod: z.enum(['email', 'einvoice']),
          billingEmail: z.string().nullable(),
          businessId: z.string().nullable(),
          members: z
            .array(
              z.object({
                id: z.number(),
                accessRole: z.enum(['owner', 'member']).nullable(),
                relationshipType: z
                  .enum(['property_manager', 'board_chair', 'board', 'other'])
                  .nullable(),
                user: z.object({
                  id: z.number(),
                  firstName: z.string(),
                  lastName: z.string(),
                  email: z.string().nullable(),
                  phoneNumber: z.string().nullable(),
                }),
              }),
            )
            .optional(),
        }),
        address: z
          .object({
            id: z.number(),
            street: z.string(),
            city: z.string(),
            postalCode: z.string().nullable(),
            country: z.string().nullable(),
            latitude: z.string().nullable(),
            longitude: z.string().nullable(),
          })
          .optional(),
      })
      .nullable()
      .optional(),
    opportunity: z
      .object({
        id: z.number(),
        stage: z.enum(opportunityStageEnum.enumValues),
        customerId: z.number().nullable(),
        user: z.object({
          id: z.number(),
          firstName: z.string(),
          lastName: z.string(),
          email: z.string().nullable(),
          phoneNumber: z.string().nullable(),
        }),
      })
      .nullable()
      .optional(),
    // Staff-only: what sales did with the upsell filed from this visit.
    internalReportedOpportunities: z
      .array(z.object({ stage: z.enum(opportunityStageEnum.enumValues) }))
      .optional(),
    visitTasks: z
      .array(
        selectVisitTaskSchema.extend({
          steps: z.array(selectVisitTaskStepSchema).optional(),
          maintenanceTask: z
            .object({
              id: z.number(),
              name: z.string(),
              description: z.string().nullable(),
              category: z.string().nullable(),
              estimatedDurationMinutes: z.number().nullable(),
              price: z.string().nullable(),
            })
            .optional(),
          billedByUser: z
            .object({
              id: z.number(),
              firstName: z.string(),
              lastName: z.string(),
            })
            .nullable()
            .optional(),
        }),
      )
      .optional(),
    visitLineItems: z.array(selectVisitLineItemWithProductSchema).optional(),
    visitAttendees: z
      .array(
        selectVisitAttendeeSchema.extend({
          user: z
            .object({
              id: z.number(),
              firstName: z.string(),
              lastName: z.string(),
              email: z.string(),
            })
            .optional(),
        }),
      )
      .optional(),
    visitCommissions: z
      .array(
        selectVisitCommissionSchema.extend({
          user: z
            .object({
              id: z.number(),
              firstName: z.string(),
              lastName: z.string(),
              email: z.string().nullable(),
            })
            .optional(),
          visitTask: z
            .object({
              id: z.number(),
              taskName: z.string(),
            })
            .nullable()
            .optional(),
        }),
      )
      .optional(),
    preInfo: selectVisitPreInfoSchema
      .extend({
        internalReviewedByUser: z
          .object({
            id: z.number(),
            firstName: z.string(),
            lastName: z.string(),
          })
          .nullable()
          .optional(),
      })
      .nullable()
      .optional(),
    feedbacks: z
      .array(
        z.object({
          id: z.number(),
          ratingValue: z.number(),
          description: z.string().nullable().optional(),
          feedbackTagMappings: z
            .array(
              z.object({
                id: z.number(),
                feedbackTagId: z.number(),
                feedbackTag: z
                  .object({
                    id: z.number(),
                    name: z.string(),
                    category: z.string().nullable().optional(),
                  })
                  .optional(),
              }),
            )
            .optional(),
        }),
      )
      .optional(),
    salesUser: z
      .object({
        id: z.number(),
        firstName: z.string(),
        lastName: z.string(),
      })
      .nullable()
      .optional(),
    internalSafetyNoticesReviewedByUser: z
      .object({
        id: z.number(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string(),
      })
      .nullable()
      .optional(),
    internalFireDepartmentNotifiedByUser: z
      .object({
        id: z.number(),
        firstName: z.string(),
        lastName: z.string(),
      })
      .nullable()
      .optional(),
    // Invoices related to this visit (PDF download via separate invoice.getInvoicePdf endpoint)
    // Soft-deleted invoices filtered automatically by addSoftDeleteFiltering
    invoices: z
      .array(
        z.object({
          id: z.number(),
          createdAt: z.string(),
          invoiceNumber: z.string(),
          referenceNumber: z.string(), // Finnish bank reference - required for payment
          totalAmount: z.string(),
          status: z.enum(['finalized', 'sent', 'paid', 'void']),
          dueDate: z.string(),
          sentAt: z.string().nullable(),
          paidAt: z.string().nullable(),
          // Invoice this one replaces (for correction invoices)
          voidedInvoice: z
            .object({
              invoiceNumber: z.string(),
            })
            .nullable()
            .optional(),
        }),
      )
      .optional(),
  }),
)

// Input schema for creating visit with attendees
export const createVisitWithAttendeesSchema = insertVisitSchema.extend({
  attendees: z
    .array(
      z.object({
        userId: z.number(),
        role: z
          .enum(['lead', 'assistant', 'trainee', 'instructor', 'participant'])
          .optional(),
        status: z.enum(['confirmed', 'tentative', 'declined']).optional(),
        notes: z.string().optional(),
        // Per-attendee booking range. Optional: when omitted, the BE trigger
        // inherits from the visit's startTime/endTime (90% case). Pass an
        // explicit range when this attendee works a different shift than the
        // visit's overall span — used by multi-day visits where the same user
        // has multiple non-overlapping attendee rows.
        // See docs/multi-day-visits-refactor.md.
        startTime: z.string().datetime().optional(),
        endTime: z.string().datetime().optional(),
      }),
    )
    .optional(),
})

// Update schemas
export const updateVisitSchema = insertVisitSchema.partial()
export const updateVisitTaskSchema = insertVisitTaskSchema.partial()
export const updateVisitAttendeeSchema = insertVisitAttendeeSchema.partial()
export const updateVisitTaskStepSchema = insertVisitTaskStepSchema.partial()

// Safety notices review schema
export const markSafetyNoticesReviewedSchema = z.object({
  id: z.number(),
  reviewComment: z.string().optional(),
})

// Fireplace selection with quantity
export const fireplaceSelectionSchema = z.object({
  type: z.string(),
  quantity: z.number().int().positive().default(1),
})

// Visit task metadata schemas (for forms and certificates)
export const nuohoustodistusDataSchema = z.object({
  // Fireplace types that were swept (with quantities)
  fireplaceTypes: z.array(fireplaceSelectionSchema),

  // Fireplaces and flues assessment
  fireplaceStatus: z.enum(['ok', 'deficiency']),
  fireplaceComments: z.string().optional(),

  // Roof safety assessment
  roofSafety: z.enum(['ok', 'deficiency', 'not_ok_not_performed']),
  roofSafetyComments: z.string().optional(),

  // Action required when deficiencies are found
  actionRequired: z
    .enum(['customer_guidance', 'notify_fire_department'])
    .optional(),

  // Next sweeping deadline. Optional because the Ajas import can only fill it
  // when the legacy form recorded a choice — a defaulted deadline on a
  // certificate nobody made a call on is a fabricated regulatory date.
  nextSweepingDeadline: z.enum(['1_year', '3_years']).optional(),

  // General inspector notes
  inspectorNotes: z.string().optional(),
})

export const visitTaskMetadataSchema = z.discriminatedUnion('formType', [
  z.object({
    formType: z.literal('nuohoustodistus'),
    data: nuohoustodistusDataSchema,
  }),
  z.object({
    formType: z.literal('mittauspoytakirja'),
    data: mittauspoytakirjaDataSchema,
  }),
])

// Public booking schemas

const getTimeSlotsForAddressBaseSchema = z.object({
  street: z.string().min(1, 'Osoite on pakollinen'),
  // Postal code is optional — Google geocodes street + city fine, and the
  // postal code only sharpens the match. Frontend already treats it as optional.
  postalCode: z.string().optional(),
  city: z.string().min(1, 'Kaupunki on pakollinen'),
  maxDate: z.string().datetime().optional(),
  /**
   * A/B test variant this client is showing, e.g. 'iv-hinta:b'. Recorded as-is
   * so searches and bookings can be counted per variant; absent when no test
   * is running. Free text, since only the site knows what it is testing.
   */
  experiment: z.string().max(64).optional(),
})

/**
 * One discriminated-union variant per preset key: the key's literal plus the
 * `presetFormValue` schema that key requires. Written as a mapped type (not a
 * plain object type) so the union stays *discriminated* — narrowing on
 * `servicePresetKey` still narrows `presetFormValue` to that preset's schema,
 * exactly as the hand-written variants did.
 */
type PresetVariant<Base extends z.ZodRawShape> = {
  [K in ServicePresetKey]: z.ZodObject<
    Base & {
      servicePresetKey: z.ZodLiteral<K>
      presetFormValue: (typeof PRESET_FORM_VALUE_SCHEMAS)[K]
    }
  >
}[ServicePresetKey]

/**
 * Builds the per-preset variants for a booking-style schema. Both public
 * booking endpoints share the preset half but not the rest of their input, so
 * the base object is a parameter.
 *
 * The cast is unavoidable and contained: `Object.entries` hands back plain
 * `string` keys, so `z.literal(key)` types as `ZodLiteral<string>` rather than
 * the specific literal. The declared return type restores the correlation the
 * runtime values already have.
 */
const presetVariants = <Base extends z.ZodRawShape>(
  base: z.ZodObject<Base>,
): [PresetVariant<Base>, ...PresetVariant<Base>[]] =>
  Object.entries(PRESET_FORM_VALUE_SCHEMAS).map(([key, presetFormValue]) =>
    base.extend({ servicePresetKey: z.literal(key), presetFormValue }),
  ) as unknown as [PresetVariant<Base>, ...PresetVariant<Base>[]]

/** Duration is computed server-side from presetFormValue — no client-supplied duration. */
export const getTimeSlotsForAddressSchema = z.discriminatedUnion(
  'servicePresetKey',
  presetVariants(getTimeSlotsForAddressBaseSchema),
)

// Common customer + address fields shared across all preset bookings
const bookServiceVisitBaseSchema = z.object({
  startTime: z.string().datetime(),
  employeeId: z.number(),
  firstName: z.string().min(1, 'Etunimi on pakollinen'),
  lastName: z.string().min(1, 'Sukunimi on pakollinen'),
  email: z.string().email('Virheellinen sähköpostiosoite'),
  phoneNumber: phoneNumberSchema,
  street: z.string().min(1, 'Osoite on pakollinen'),
  postalCode: z.string().min(1, 'Postinumero on pakollinen'),
  city: z.string().min(1, 'Kaupunki on pakollinen'),
  propertyType: z.enum(propertyTypeEnum.enumValues, {
    errorMap: () => ({ message: 'Kiinteistön tyyppi on pakollinen' }),
  }),
  notes: z.string().optional(),
  /**
   * Optional discount code the customer entered. Validated server-side against
   * the `discount_codes` table (existence + validity window); the resulting
   * discount is recorded as a negative visit line item so it shows on the
   * invoice as a distinct "-X €" row. An invalid/expired code rejects the
   * whole booking before any side effects.
   */
  discountCode: z.string().optional(),
  /**
   * Canonical attribution payload from the client's mk_attr cookie.
   * firstTouch → customer.internalFirstTouchAcquisition (via user), lastTouch
   * → visit.internalAcquisitionData. See docs/attribution-semantics.md §3.
   */
  attribution: attributionPayloadSchema.optional(),
  /**
   * Legacy flat-UTM field — MUST remain until the `booking-widget-inline.iife.js`
   * bundles currently shipped on markku.fi and nuohousmarkku.fi are rebuilt
   * from this branch's source and redeployed. Those bundles send
   * `{ utmParams: {...} }` and without this field the strict schema would
   * 400 every live booking. Remove after both sites ship the new IIFE.
   */
  utmParams: z.record(z.string()).optional(),
  /**
   * Window-booking presets only. 'window' books the arrival window given by
   * `arrivalWindowStart/End`: the server picks the provisional time by first
   * fit, and takes `startTime` — the search's `provisionalStartTime` — only
   * where the packer calls the window full; 'exact' books `startTime` as the
   * fixed time the search offered and adds the preset's surcharge. Absent = legacy exact-time
   * booking without surcharge — the booking-widget bundles deployed on
   * markku.fi / nuohousmarkku.fi before this field existed still send that
   * shape. Make it required for window presets once both sites are rebuilt.
   */
  timePreference: z.enum(['window', 'exact']).optional(),
  arrivalWindowStart: z.string().datetime().optional(),
  arrivalWindowEnd: z.string().datetime().optional(),
  /**
   * A/B test variant this client is showing, e.g. 'iv-hinta:b'. Recorded as-is
   * so searches and bookings can be counted per variant; absent when no test
   * is running. Free text, since only the site knows what it is testing.
   */
  experiment: z.string().max(64).optional(),
  /** The `seedArea.id` of the chosen time or window, so the booking moves its day there. */
  seedServiceAreaId: z.number().optional(),
})

// Server-authoritative booking schema: the client picks a preset and sends
// only the form values they chose. The backend computes tasks, duration, and
// per-task pricing from those values, so the client cannot manipulate the
// price, the task IDs, or the visit length.
//
// Default Zod behavior (strip) is used deliberately — NOT `.strict()`.
// Unknown fields from stale embedded bundles (e.g. markku.fi /
// nuohousmarkku.fi's cached booking-widget-inline.iife.js that may ship a
// new field before the backend knows about it) are silently dropped rather
// than 400'd. Server-authoritative pricing still holds: since `price`,
// `maintenanceTaskIds`, `durationMinutes` etc. aren't in the schema, Zod
// strips them before the handler sees them — client can't smuggle them in.
// Strict mode would only loudly reject instead of silently stripping; same
// security, worse resilience.
export const bookServiceVisitSchema = z.discriminatedUnion(
  'servicePresetKey',
  presetVariants(bookServiceVisitBaseSchema),
)

/**
 * Set when the time is on an empty day the search moved to a flex area for a
 * customer nothing else could serve; booking it moves the day there.
 */
const seedAreaSchema = z.object({ id: z.number(), name: z.string() })

export const availableTimeSlotSchema = z.object({
  startTime: z.string().datetime(),
  endTime: z.string().datetime(),
  userId: z.number(),
  /** The free helper the slot was found with, on `requiresHelper` presets. */
  helperUserId: z.number().optional(),
  seedArea: seedAreaSchema.optional(),
})

/**
 * One bookable arrival window for a window-booking preset. `userId` is the
 * provisional (closest) employee the booking lands on; `remainingSlots` counts
 * how many more jobs of the requested size fit across every employee in range,
 * and `exactOptions` are the fixed-time alternatives (window edges) sold with
 * the preset's surcharge.
 */
export const bookingWindowSchema = z.object({
  key: z.string(),
  label: z.string(),
  windowStart: z.string().datetime(),
  windowEnd: z.string().datetime(),
  userId: z.number(),
  remainingSlots: z.number().int(),
  /** Sweepers who can still take a job in this window — the assignment itself
   * is the route planner's, so no single name is promised. */
  employeeCount: z.number().int(),
  /**
   * Where a window booking would provisionally land on `userId` right now:
   * first fit, or — where the packer calls the window full — the one place
   * the reachability search verified. Null when neither exists. Bookings send
   * it back as `startTime`.
   */
  provisionalStartTime: z.string().datetime().nullable(),
  provisionalEndTime: z.string().datetime().nullable(),
  exactOptions: z.array(availableTimeSlotSchema),
  /** The seeded flex area of `userId`'s day; see `availableTimeSlotSchema`. */
  seedArea: seedAreaSchema.optional(),
})

// Type exports
export type Visit = z.infer<typeof selectVisitSchema>
export type VisitWithRelations = z.infer<typeof selectVisitWithRelationsSchema>
// A visit that has been scheduled: startTime/endTime are guaranteed non-null.
// Narrow to this at fetch boundaries that exclude unscheduled visits (e.g. the
// timeline/day queries that filter on a time range) so downstream components
// don't need null checks. The DB CHECK constraint guarantees a non-cancelled
// visit with times has both set; we still verify both when narrowing.
export type ScheduledVisit = VisitWithRelations & {
  startTime: string
  endTime: string
}
// Structural guard for any row with nullable start/end times — narrows both to
// non-null. Works on backend query projections as well as full visit objects.
// Use as a `.filter()` predicate to drop unscheduled rows from a list, or with
// `isScheduledVisit` below for full visits.
export const hasScheduledTimes = <
  T extends { startTime: string | null; endTime: string | null },
>(
  row: T,
): row is T & { startTime: string; endTime: string } =>
  row.startTime !== null && row.endTime !== null
// Narrow a visit to ScheduledVisit. Use at fetch boundaries (lists filtered by
// time range, or getById behind a "scheduled" guard) so downstream components
// receive non-null times without per-site assertions.
export const isScheduledVisit = (
  visit: VisitWithRelations,
): visit is ScheduledVisit => hasScheduledTimes(visit)

// Who may be handed a to-do on this visit: only its attendees can open the
// visit page the task links to.
export const visitAttendeeUsers = (visit: {
  visitAttendees?:
    | {
        userId: number
        user?: { id: number; firstName: string; lastName: string }
      }[]
    | null
}) => [
  ...new Map(
    (visit.visitAttendees ?? []).flatMap((a) =>
      a.user ? [[a.userId, a.user] as const] : [],
    ),
  ).values(),
]

/** Maintenance task ids on a visit, for the service-preset predicates. */
export const visitTaskIds = (visit: {
  visitTasks?: { maintenanceTaskId: number | null }[] | null
}): number[] =>
  (visit.visitTasks ?? [])
    .map((task) => task.maintenanceTaskId)
    .filter((id): id is number => id !== null)

/**
 * Ventilation work asks the customer for pre-visit info, so the panel shows
 * from the moment the visit exists — an officer taking a phone booking can
 * copy the link mid-call — and stays once a questionnaire exists even if the
 * tasks change.
 */
export const visitNeedsPreInfo = (
  visit: Pick<VisitWithRelations, 'preInfo' | 'visitTasks'>,
): boolean => !!visit.preInfo || isVentilationVisit(visitTaskIds(visit))

/**
 * Which optional sections the visit view offers. Each is `own data || the work
 * calls for it`: the data branch keeps an entry written before the tasks
 * changed reachable, so nothing a worker filled in can be stranded.
 *
 * These gate on `isVentilationWork`, not the narrower preset list behind
 * `visitNeedsPreInfo` — offering a worker a section is free, whereas the
 * questionnaire mails the customer.
 */
export const visitNeedsMeasurementReport = (
  visit: Pick<VisitWithRelations, 'measurementReportData' | 'visitTasks'>,
): boolean =>
  !!visit.measurementReportData?.length ||
  isVentilationWork(visitTaskIds(visit))

export const visitNeedsWorkContent = (
  visit: Pick<VisitWithRelations, 'workContent' | 'visitTasks'>,
): boolean =>
  !!visit.workContent?.length || isVentilationWork(visitTaskIds(visit))

/**
 * A free-text upsell (sales notes or upsell photos) becomes a sales
 * opportunity on completion.
 */
// visit_commissions.notes value that marks the reporter's recommendation
// bonus, as opposed to their ordinary worker share on the same visit.
export const RECOMMENDATION_BONUS_NOTE = 'Maintenance recommendation'

export const hasUpsellRecorded = (
  visit: Pick<VisitWithRelations, 'internalSalesNotes' | 'attachments'>,
): boolean =>
  !!visit.internalSalesNotes?.trim() ||
  !!visit.attachments?.some((a) => a.metadata?.type === 'upsell')

/**
 * A visit with no subscription and nothing to invoice is almost always a
 * forgotten price, so a field worker must give a reason to complete it.
 * Subscription work is 0 € to the customer on purpose; `tbd` and unpriced
 * billable line items are priced later in the office; a discount-code line
 * explains a 0 € total, it doesn't mean the price was forgotten. Task prices
 * are gross, line items net.
 */
export const isUnbilledVisit = (visit: {
  eventType: VisitWithRelations['eventType']
  subscriptionId: number | null
  visitTasks?: Pick<VisitTask, 'billingType' | 'price' | 'discountPercent'>[]
  visitLineItems?: Pick<
    VisitLineItem,
    'billingType' | 'quantity' | 'unitPriceVat0' | 'discountCodeId'
  >[]
}): boolean => {
  const tasks = visit.visitTasks ?? []
  const lineItems = (visit.visitLineItems ?? []).filter(
    (li) => !li.discountCodeId,
  )
  if (
    visit.eventType !== 'visit' ||
    visit.subscriptionId ||
    tasks.some((t) => t.billingType === 'subscription_included') ||
    lineItems.some(
      (li) =>
        li.billingType === 'tbd' ||
        (li.billingType === 'billable' && li.unitPriceVat0 === null),
    )
  ) {
    return false
  }
  const taskTotal = sumBy(
    tasks.filter((t) => t.billingType === 'separately_invoiced'),
    (t) => removeVat(calculateFinalPrice(t.price, t.discountPercent)),
  )
  const lineItemTotal = sumBy(
    lineItems.filter((li) => li.billingType === 'billable'),
    (li) => li.quantity * Number(li.unitPriceVat0),
  )
  return taskTotal + lineItemTotal <= 0
}

// Finnish on purpose: the office reads the reason and searches for the prefix.
export const UNBILLED_REASON_PREFIX = 'Ei laskutettavaa:'

export const hasUnbilledReason = (notes: string | null | undefined) =>
  !!notes
    ?.split('\n')
    .some(
      (line) =>
        line.startsWith(UNBILLED_REASON_PREFIX) &&
        !!line.slice(UNBILLED_REASON_PREFIX.length).trim(),
    )

/** The visit's video summary, if one was recorded. */
export const visitVideoAttachment = (
  visit: Pick<VisitWithRelations, 'attachments'>,
) => visit.attachments?.find((a) => a.metadata?.type === 'visit_video')

export type VisitTask = z.infer<typeof selectVisitTaskSchema>
export type VisitTaskStep = z.infer<typeof selectVisitTaskStepSchema>
export type VisitTaskWithSteps = z.infer<typeof selectVisitTaskWithStepsSchema>
export type VisitAttendee = z.infer<typeof selectVisitAttendeeSchema>

// Backend virtual filters on visit.list (subqueries on related tables), not
// visits columns. Only the { eq: value } form is accepted — see
// createCrudRouter's virtualFilters option.
export type VisitVirtualFilters = {
  attendeeUserId?: number
  preInfoStatus?: string
  attendeeRangeOverlaps?: TimeRange
}

export type CreateVisit = z.infer<typeof insertVisitSchema>
export type UpdateVisit = z.infer<typeof updateVisitSchema>
export type CreateVisitTask = z.infer<typeof insertVisitTaskSchema>
export type UpdateVisitTask = z.infer<typeof updateVisitTaskSchema>
export type CreateVisitTaskStep = z.infer<typeof insertVisitTaskStepSchema>
export type UpdateVisitTaskStep = z.infer<typeof updateVisitTaskStepSchema>
export type CreateVisitWithAttendees = z.infer<
  typeof createVisitWithAttendeesSchema
>
export type BookServiceVisit = z.infer<typeof bookServiceVisitSchema>
export type GetTimeSlotsForAddress = z.infer<
  typeof getTimeSlotsForAddressSchema
>
/** One sweeper's planned day, for the availability map's plan layer. */
export const dayRoutePlanSchema = z.object({
  dateKey: z.string(),
  /** Day before at this Helsinki hour the still-flexible times get locked. */
  lockDeadline: z.string().datetime(),
  employees: z.array(
    z.object({
      userId: z.number(),
      name: z.string(),
      /** False: booked stops but no availability that day — needs a human. */
      onShift: z.boolean(),
      anchor: z
        .object({ latitude: z.number(), longitude: z.number() })
        .nullable(),
      windows: z.array(
        z.object({
          key: z.string(),
          label: z.string(),
          windowStart: z.string().datetime(),
          windowEnd: z.string().datetime(),
          booked: z.number().int(),
          remaining: z.number().int(),
          flexible: z.number().int(),
        }),
      ),
      stops: z.array(
        z.object({
          visitId: z.number(),
          title: z.string().nullable(),
          address: z.string().nullable(),
          startTime: z.string().datetime(),
          endTime: z.string().datetime(),
          latitude: z.number().nullable(),
          longitude: z.number().nullable(),
          flexible: z.boolean(),
          arrivalWindow: z.string().nullable(),
          timeLockedAt: z.string().nullable(),
          /** Time-confirmed message queue state per channel; null = none. */
          notified: z.object({
            sms: z.string().nullable(),
            email: z.string().nullable(),
          }),
        }),
      ),
    }),
  ),
})

export type AvailableTimeSlot = z.infer<typeof availableTimeSlotSchema>
export type BookingWindow = z.infer<typeof bookingWindowSchema>
export type DayRoutePlan = z.infer<typeof dayRoutePlanSchema>
export type FireplaceSelection = z.infer<typeof fireplaceSelectionSchema>
export type NuohoustodistusData = z.infer<typeof nuohoustodistusDataSchema>
export type AirMeasurement = z.infer<typeof airMeasurementSchema>
export type ExhaustAirMeasurement = z.infer<typeof exhaustAirMeasurementSchema>
export type MeasurementRoom = z.infer<typeof measurementRoomSchema>
export type MittauspoytakirjaData = z.infer<typeof mittauspoytakirjaDataSchema>
export type SaveMeasurementReport = z.infer<typeof saveMeasurementReportSchema>
export type DeleteMeasurementReport = z.infer<
  typeof deleteMeasurementReportSchema
>
export type VisitTaskMetadata = z.infer<typeof visitTaskMetadataSchema>
export type VisitCommission = z.infer<typeof selectVisitCommissionSchema>
export type CreateVisitCommission = z.infer<typeof insertVisitCommissionSchema>
export type UpdateVisitCommission = z.infer<typeof updateVisitCommissionSchema>
export type MarkSafetyNoticesReviewed = z.infer<
  typeof markSafetyNoticesReviewedSchema
>

// When the certificate PDF promises the customer a rescue-department report.
// `actionRequired` survives a deficiency being cleared, so the choice only
// counts while a deficiency is still recorded. Mirrored in SQL by
// fireDepartmentVisitIds.ts.
export const requiresFireDepartmentNotification = (
  data: Partial<NuohoustodistusData>,
) =>
  data.actionRequired === 'notify_fire_department' &&
  (data.fireplaceStatus === 'deficiency' || data.roofSafety === 'deficiency')
